import { supabase } from "@/integrations/supabase/client";

export type CloudProvider = "word" | "google";

export interface CloudTemplate {
  id: string;
  name: string;
  modified_at?: string;
  web_url?: string;
  size?: number;
}

export async function listCloudTemplates(provider: CloudProvider): Promise<CloudTemplate[]> {
  const timeoutMs = 30000;
  const call = supabase.functions.invoke("cloud-template-list", { body: { provider } });
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new CloudTemplateError(
      `Le serveur ne répond pas (>${timeoutMs / 1000}s). Réessayez ou vérifiez votre connexion ${provider === "word" ? "Microsoft" : "Google"}.`,
      "client_timeout",
    )), timeoutMs),
  );
  const { data, error } = await Promise.race([call, timeout]) as Awaited<typeof call>;
  if (error) {
    const p = await parseErrorContext(error);
    throw new CloudTemplateError(p.message, p.step, p.detail);
  }
  return (data?.templates as CloudTemplate[]) || [];
}

/** Error thrown by cloud-template actions, carrying the failing step + detail. */
export class CloudTemplateError extends Error {
  step?: string;
  detail?: string;
  constructor(message: string, step?: string, detail?: string) {
    super(message);
    this.name = "CloudTemplateError";
    this.step = step;
    this.detail = detail;
  }
}

async function parseErrorContext(error: any): Promise<{ message: string; step?: string; detail?: string }> {
  const ctx: any = error?.context;
  let parsed: any = null;
  if (ctx?.json) { try { parsed = await ctx.json(); } catch { /* empty */ } }
  return {
    message: parsed?.error || error?.message || "Erreur inconnue",
    step: parsed?.step,
    detail: parsed?.detail,
  };
}

export async function detectTemplatePlaceholders(
  provider: CloudProvider,
  templateId: string,
): Promise<string[]> {
  const { data, error } = await supabase.functions.invoke("cloud-template-merge", {
    body: { provider, template_id: templateId, action: "detect" },
  });
  if (error) {
    const p = await parseErrorContext(error);
    throw new CloudTemplateError(p.message, p.step, p.detail);
  }
  return (data?.fields as string[]) || [];
}

export async function mergeCloudTemplate(args: {
  provider: CloudProvider;
  templateId: string;
  values: Record<string, string>;
  contentMarkdown: string | null;
  filename: string;
}): Promise<{ webUrl: string }> {
  const { data, error } = await supabase.functions.invoke("cloud-template-merge", {
    body: {
      provider: args.provider,
      template_id: args.templateId,
      action: "merge",
      values: args.values,
      content_markdown: args.contentMarkdown,
      filename: args.filename,
    },
  });
  if (error) {
    const p = await parseErrorContext(error);
    throw new CloudTemplateError(p.message, p.step, p.detail);
  }
  if (!data?.webUrl) throw new CloudTemplateError("Aucune URL retournée", "no_weburl");
  return { webUrl: data.webUrl as string };
}

